// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// COMPONENT IMPORTS
import Button from "components/form/Button";
import InputField from "components/form/InputField";
import { formatCurrency, useOutsideClick } from "utils/HelperFunctions";
import { getShellZoom } from "utils/shellZoom";
import { useTid } from "utils/testid";

// ICONS IMPORTS
import { CloseCircleIcon } from "icons/payments/CloseCircleIcon";
import { AddCircleIcon } from "icons/payments/AddCircleIcon";
import { ArrowUpIcon } from "icons/payments/ArrowUpIcon";
import { ArrowDownIcon } from "icons/payments/ArrowDownIcon";
import { ArrowReplyIcon } from "icons/payments/ArrowReplyIcon";

// STYLE IMPORTS
import "../../assets/css/CommenInput.css";

// PROPS TYPE
interface AmountFilterProps {
  label: string;
  options: any;
  amount: any;
  setAmount?: any;
  isRight?: boolean;
  onOpen?: () => void;
}

/* ------------------------------------- AMOUNT FILTER ------------------------------------- */

const AmountFilter = (props: AmountFilterProps) => {
  const tid = useTid();
  const {
    label = "label",
    options,
    setAmount,
    isRight = false,
    onOpen = () => {},
  } = props;
  const amtDropDownRef = useRef<any>();
  const openRef = useRef<any>();
  const portalDropdownRef = useRef<any>();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        amtDropDownRef.current &&
        !amtDropDownRef.current.contains(e.target) &&
        (!portalDropdownRef.current ||
          !portalDropdownRef.current.contains(e.target))
      ) {
        setIsOpenDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useOutsideClick(openRef, () => {
    setIsOpen(false);
  });

  const [isOpenDropDown, setIsOpenDropDown] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [select, setSelect] = useState<any>();
  const [amtVal, setAmtVal] = useState<any>();
  const [betweenVal, setBetweenVal] = useState<any>({
    start: undefined,
    end: undefined,
  });
  const [showLabel, setShowLable] = useState<string | null>(null);
  const [filterData, setFilterData] = useState(options);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 350 });

  const updateDropdownPosition = () => {
    if (!amtDropDownRef.current) return;

    // Portalled and scaled: the measured rect is real px, the inline
    // coordinates are read as design px. See utils/shellZoom.
    const zoom = getShellZoom(amtDropDownRef.current);
    const rect = amtDropDownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth / zoom;
    const viewportPadding = 12;
    const dropdownWidth = isRight
      ? 260
      : Math.min(350, viewportWidth - viewportPadding * 2);
    const preferredLeft = isRight
      ? rect.right / zoom - dropdownWidth
      : rect.left / zoom;
    const maxLeft = viewportWidth - dropdownWidth - viewportPadding;

    setDropdownPos({
      top: rect.bottom / zoom + 5,
      left: Math.max(viewportPadding, Math.min(preferredLeft, maxLeft)),
      width: dropdownWidth,
    });
  };

  useEffect(() => {
    setFilterData(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (!isOpenDropDown) return;

    const handleResize = () => {
      updateDropdownPosition();
    };

    const handleScroll = () => {
      setIsOpenDropDown(false);
      setIsOpen(false);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpenDropDown, isRight]);

  const handleStatusChange = (e: any, isCheckbox: any, c: any) => {
    if (isCheckbox) {
      if (e.target.checked) {
        setSelect(c);
        setIsOpen(false);
      } else {
        setSelect(null);
      }
    } else {
      if (select && select === c) {
        setSelect(null);
      } else {
        setSelect(c);
        setIsOpen(false);
      }
    }
    e.stopPropagation();
    e.preventDefault();
  };

  const setFinalval = () => {
    let keyName = filterData?.find((x: any) => x.name === select);
    if (select === "is between") {
      const valuePair = {
        [keyName?.value]: [
          parseFloat(betweenVal?.start),
          parseFloat(betweenVal?.end),
        ],
      };
      setShowLable(
        `${formatCurrency(betweenVal?.start)} to ${formatCurrency(
          betweenVal?.end
        )}`
      );
      setAmount({ ...valuePair });
    } else {
      const valuePair = {
        [keyName?.value]: parseFloat(amtVal),
      };
      setShowLable(`${keyName?.label} ${formatCurrency(amtVal)}`);
      setAmount({ ...valuePair });
    }
    setIsOpenDropDown(false);
  };

  return (
    <>
      <div className="relative w-full" ref={amtDropDownRef}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpenDropDown}
          aria-haspopup="dialog"
          aria-label={`${label} filter`}
          data-testid={tid(`${label}-filter-button`)}
          className="rounded-[20px] w-full min-w-0 hover:bg-bgHover border-[1px] border-strokePrimary bg-bgPrimary h-[34px] flex items-center gap-[6px] pl-[9px] pr-[15px] cursor-pointer "
          onClick={() => {
            if (!isOpenDropDown && amtDropDownRef.current) {
              onOpen();
              setTimeout(() => {
                updateDropdownPosition();
                setIsOpenDropDown(true);
              }, 350);
            } else {
              setIsOpenDropDown(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            if (!isOpenDropDown && amtDropDownRef.current) {
              onOpen();
              setTimeout(() => {
                updateDropdownPosition();
                setIsOpenDropDown(true);
              }, 350);
            } else {
              setIsOpenDropDown(false);
            }
          }}
        >
          <div
            role={showLabel ? "button" : undefined}
            tabIndex={showLabel ? 0 : undefined}
            aria-label={showLabel ? `Clear ${label} filter` : undefined}
            data-testid={tid(`${label}-filter-icon`)}
            className="cursor-pointer shrink-0"
            onClick={(e) => {
              if (showLabel) {
                e.stopPropagation();
                e.preventDefault();
                setAmount(undefined);
                setSelect(null);
                setShowLable(null);
                setAmtVal(null);
                setBetweenVal({ start: undefined, end: undefined });
              }
            }}
            onKeyDown={(e) => {
              if (!showLabel) return;
              if (e.key !== "Enter" && e.key !== " ") return;
              e.stopPropagation();
              e.preventDefault();
              setAmount(undefined);
              setSelect(null);
              setShowLable(null);
              setAmtVal(null);
              setBetweenVal({ start: undefined, end: undefined });
            }}
          >
            {showLabel ? <CloseCircleIcon /> : <AddCircleIcon />}
          </div>
          <div className="text-txtParagraph leading-normal font-medium text-xs whitespace-nowrap shrink-0">
            {label}
          </div>
          {showLabel && (
            <div className="h-[16px] w-[1px] bg-txtParagraph shrink-0" />
          )}
          {showLabel && (
            <div
              title={showLabel}
              className="md:text-sm text-xs font-bold text-txtPrimary leading-normal truncate min-w-0"
            >
              {showLabel}
            </div>
          )}
        </div>
        {isOpenDropDown &&
          ReactDOM.createPortal(
            <div
              ref={portalDropdownRef}
              role="dialog"
              aria-label={`${label} filter options`}
              data-testid={tid(`${label}-filter-popover`)}
              style={{
                position: "fixed",
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                zIndex: 9999,
              }}
              className={`scaled-portal ${
                isRight ? "max-w-[260px]" : "max-w-[350px]"
              } bg-bgPrimary shadow-[0px_0px_12px_-3px_#DFE4EC] rounded-[7px]`}
            >
              <div className="py-[11px] px-[15px] text-sm font-normal leading-normal text-txtPrimary">
                Filter by Amount
              </div>
              <div className="py-[11px] px-[15px]">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  aria-label={`${label} comparison selector`}
                  data-testid={tid(`${label}-comparison-button`)}
                  className="w-full px-3 py-[6px] bg-bgPrimary border border-strokePrimary hover:!border-strokeActive  rounded flex justify-between cursor-pointer items-center relative"
                  ref={openRef}
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <div className="text-sm  font-normal leading-normal text-txtPrimary ">
                    {select}
                  </div>
                  <div
                    aria-hidden="true"
                    className="flex flex-col gap-[-2px] cursor-pointer"
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    <ArrowUpIcon />
                    <ArrowDownIcon />
                  </div>
                  {isOpen ? (
                    <div className="absolute bg-bgPrimary shadow-[0px_0px_12px_-3px_#DFE4EC] z-40 rounded-[7px] w-[320px] top-[43px] left-0">
                      <div className={`max-h-[200px] overflow-y-auto`}>
                        {filterData?.map((data: any) => {
                          return (
                            <div
                              key={data?.value || data?.name}
                              data-testid={tid(
                                `${label}-comparison-option-${data?.value || data?.name}`
                              )}
                              onClick={(e) =>
                                handleStatusChange(e, false, data?.name)
                              }
                            >
                              <div className="flex items-center gap-[7px] h-[40px] px-[15px] py-[11px] leading-normal border-b border-b-strokePrimary hover:bg-bgHover">
                                <input
                                  aria-label={`${data?.name} amount comparison`}
                                  data-testid={tid(
                                    `${label}-comparison-checkbox-${data?.value || data?.name}`
                                  )}
                                  type="checkbox"
                                  checked={select === data?.name}
                                  onChange={(e) =>
                                    handleStatusChange(e, true, data?.name)
                                  }
                                  className="dcdial_portal_checkbox"
                                />
                                <div
                                  className="text-txtPrimary text-sm font-normal cursor-pointer"
                                  onClick={(e) => {
                                    handleStatusChange(e, false, data?.name);
                                  }}
                                >
                                  {data?.name}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {select ? (
                <div className="pb-[11px] px-[15px] flex items-center gap-3 animate__animated animate__fadeIn">
                  <ArrowReplyIcon />
                  {select !== "is between" ? (
                    <InputField
                      name="amtVal"
                      data-testid={tid(`${label}-value-input`)}
                      aria-label={`${label} amount`}
                      value={amtVal}
                      showLabel={false}
                      placeholder=""
                      type="number"
                      className="!px-3 !py-[6px] !w-[284px]"
                      onChange={(e) => setAmtVal(e.target.value)}
                    />
                  ) : (
                    <>
                      <InputField
                        name="start"
                        data-testid={tid(`${label}-start-input`)}
                        aria-label={`${label} start amount`}
                        value={betweenVal?.start}
                        showLabel={false}
                        placeholder=""
                        type="number"
                        className="!px-3 !py-[6px]"
                        onChange={(e) => {
                          setBetweenVal({
                            ...betweenVal,
                            start: e.target.value,
                          });
                        }}
                      />
                      <div className="text-sm font-normal leading-normal text-txtPrimary">
                        and
                      </div>
                      <InputField
                        name="end"
                        data-testid={tid(`${label}-end-input`)}
                        aria-label={`${label} end amount`}
                        value={betweenVal?.end}
                        showLabel={false}
                        placeholder=""
                        type="number"
                        className="!px-3 !py-[6px]"
                        onChange={(e) => {
                          setBetweenVal({ ...betweenVal, end: e.target.value });
                        }}
                      />
                    </>
                  )}
                </div>
              ) : null}
              <div className="py-[11px] px-[15px]">
                <Button
                  text="Apply"
                  data-testid={tid(`${label}-filter-apply-button`)}
                  aria-label={`Apply ${label} filter`}
                  className="w-full"
                  isDisabled={
                    !select ||
                    (select === "is between"
                      ? !betweenVal?.start || !betweenVal?.end
                      : !amtVal)
                  }
                  onClick={() => setFinalval()}
                />
              </div>
            </div>,
            document.body
          )}
      </div>
    </>
  );
};

export default AmountFilter;
